/**
 * Native date formatting utilities to replace moment.js locale-based formatting
 * Uses Intl.DateTimeFormat for locale-aware formatting without external dependencies
 */

import { fromUnixTimestamp } from '@utils/date-manipulation';

import { extractDateTimeParts } from './extract-date-time-parts';

/**
 * Moment.js format tokens mapped to Intl.DateTimeFormat options
 */
const MOMENT_FORMAT_TOKENS: Record<string, Intl.DateTimeFormatOptions> = {
  // Date formats
  'ddd, MMMM D, YYYY': { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' },
  'ddd DD MMM YY': { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' },
  'ddd DD MMM': { weekday: 'short', day: '2-digit', month: 'short' },
  'ddd DD': { weekday: 'short', day: '2-digit' },
  'MMMM YY': { month: 'long', year: 'numeric' },
  'MMM Do, HH:mm': { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },

  // Single tokens
  D: { day: 'numeric' }, // 1, 2... 31
  DD: { day: '2-digit' }, // 01, 02... 31
  dd: { weekday: 'short' }, // Su, Mo... (Intl 'short' is Sun, Mon) - format-date.ts uses charAt(0) so this is fine
  ddd: { weekday: 'short' }, // Sun, Mon...
  MMM: { month: 'short' }, // Jan, Feb...
  MMMM: { month: 'long' }, // January, February...
  YY: { year: '2-digit' }, // 70, 71... 29
  YYYY: { year: 'numeric' }, // 1970, 1971... 2029

  // Moment's localized formats
  LL: { dateStyle: 'long' }, // e.g., "January 29, 2026"
  LLL: { dateStyle: 'long', timeStyle: 'short' }, // e.g., "January 29, 2026, 10:30 AM"
  'HH:mm': { hour: '2-digit', minute: '2-digit', hour12: false },
};

/**
 * Capitalizes the first letter of a string
 * Handles special cases like removing dots and extra spaces
 */
const capitalize = (str: string): string => {
  if (!str) return str;
  // Remove dots and trim
  const cleaned = str.replace(/\./g, '').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

/**
 * Custom format parser for moment-like format strings
 */
const parseCustomFormat = (date: Date, format: string, locale: string, timezone?: string): string => {
  // Handle special case for "ddd, MMMM D, YYYY"
  if (format === 'ddd, MMMM D, YYYY') {
    const weekday = capitalize(
      date.toLocaleDateString(locale, {
        weekday: 'short',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const month = capitalize(
      date.toLocaleDateString(locale, {
        month: 'long',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const day = date.toLocaleDateString(locale, {
      day: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    const year = date.toLocaleDateString(locale, {
      year: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    return `${weekday}, ${month} ${day}, ${year}`;
  }

  // Handle "MMM Do, HH:mm" format
  if (format === 'MMM Do, HH:mm' || format === ' MMM Do, HH:mm') {
    const month = capitalize(
      date.toLocaleDateString(locale, {
        month: 'short',
        ...(timezone && { timeZone: timezone }),
      })
    );
    const day = date.toLocaleDateString(locale, {
      day: 'numeric',
      ...(timezone && { timeZone: timezone }),
    });
    const time = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...(timezone && { timeZone: timezone }),
    });
    return `${month} ${day}, ${time}`;
  }

  // Default: try to use predefined format
  const options = MOMENT_FORMAT_TOKENS[format];
  if (options) {
    return date.toLocaleString(locale, {
      ...options,
      ...(timezone && { timeZone: timezone }),
    });
  }

  // Fallback to ISO string
  return date.toISOString();
};

/**
 * Format options for the formatDateTime function
 */
export interface FormatDateTimeOptions {
  /** The date to format (can be Date object or Unix timestamp in seconds) */
  date: Date | number;
  /** The locale to use for formatting (e.g., 'en', 'es', 'ca') */
  locale: string;
  /** Moment.js format string or predefined format key */
  format: string;
  /** Optional timezone for formatting */
  timezone?: string;
}

/**
 * Formats a date using native Intl.DateTimeFormat with locale support
 * Replaces moment's .locale().format() pattern
 *
 * @example
 * // Format with custom moment-like format
 * formatDateTime({
 *   date: new Date(),
 *   locale: 'es',
 *   format: 'ddd, MMMM D, YYYY'
 * })
 * // Returns: "lun, enero 29, 2026"
 *
 * @example
 * // Format with timezone
 * formatDateTime({
 *   date: 1706515200,
 *   locale: 'en',
 *   format: 'LLL',
 *   timezone: 'Europe/Madrid'
 * })
 * // Returns: "January 29, 2024, 10:00 AM"
 */
export const formatDateTime = ({ date, locale, format, timezone }: FormatDateTimeOptions): string => {
  // Convert Unix timestamp to Date if needed
  const dateObj = typeof date === 'number' ? fromUnixTimestamp(date) : date;

  // Use custom parser for complex formats
  return parseCustomFormat(dateObj, format, locale, timezone);
};

/**
 * Format a date to a localized date string
 *
 * @example
 * formatDate(new Date(), 'es', { dateStyle: 'long' })
 * // Returns: "29 de enero de 2026"
 */
const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

export const formatDate = (
  date: Date | number,
  locale: string,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
  timezone?: string
): string => {
  const dateObj = typeof date === 'number' ? fromUnixTimestamp(date) : date;
  return dateObj.toLocaleDateString(locale, {
    ...options,
    ...(timezone && { timeZone: timezone }),
  });
};

/**
 * Format a date to a localized time string
 *
 * @example
 * formatTime(new Date(), 'es', { hour: '2-digit', minute: '2-digit' })
 * // Returns: "10:30"
 */
const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

export const formatTime = (
  date: Date | number,
  locale: string,
  options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS,
  timezone?: string
): string => {
  const dateObj = typeof date === 'number' ? fromUnixTimestamp(date) : date;
  return dateObj.toLocaleTimeString(locale, {
    ...options,
    ...(timezone && { timeZone: timezone }),
  });
};

/**
 * Format a date with name for event selectors
 * Returns format: "Fri02 - Event Name"
 *
 * @example
 * formatDateName('My Event', 1706515200, 'Europe/Madrid', 'en')
 * // Returns: "Fri02 - My Event"
 */
export const formatDateName = (name: string, startingTime: number, timezone: string, locale: string = 'en') => {
  const parts = extractDateTimeParts(startingTime, locale, timezone, { dateStyle: 'veryShort' });
  return `${parts.weekday}${parts.day} - ${name}`;
};
